import { motion } from 'framer-motion';
import { Landmark, Fingerprint, ShieldCheck, Scroll, BookMarked, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const RIGHTS = [
  {
    icon: Fingerprint,
    citation: 'Section 12',
    title: 'Freedom & security of the person',
    body: 'Everyone has the right not to be deprived of freedom arbitrarily or without just cause. An arrest that fails these constitutional guardrails may found a civil claim.',
  },
  {
    icon: ShieldCheck,
    citation: 'Section 35',
    title: 'Rights of arrested persons',
    body: 'Includes the right to remain silent, be informed of the reason for arrest, consult an attorney, and be brought before a court as soon as reasonably possible (and not later than 48 hours).',
  },
  {
    icon: Scroll,
    citation: 'CPA s 40',
    title: 'Warrantless arrests are strictly limited',
    body: 'A peace officer may arrest without a warrant only where narrow statutory grounds exist. Where those grounds are absent or improperly applied, the arrest may be unlawful.',
  },
  {
    icon: BookMarked,
    citation: 'Prescription Act',
    title: 'Prescription — often 3 years',
    body: 'Civil claims arising from unlawful arrest and detention are generally subject to a 3-year prescription period under the Prescription Act 68 of 1969, subject to the facts.',
  },
];

export default function ConstitutionalRights() {
  return (
    <section data-testid="constitutional-rights" className="relative py-24 md:py-32 border-t border-white/5 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute left-1/2 top-0 -translate-x-1/2 w-[80%] h-[420px] bg-[radial-gradient(60%_60%_at_50%_0%,rgba(10,132,255,0.18),transparent_70%)]" />
      </div>
      <div className="relative mx-auto max-w-7xl px-5 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-end mb-14">
          <div className="lg:col-span-7">
            <div className="text-[11px] font-inter font-bold uppercase tracking-[0.28em] text-[#FFD400]">Chapter 03 — The Constitution</div>
            <h2 className="mt-4 font-poppins font-bold text-white text-4xl md:text-5xl lg:text-6xl tracking-[-0.02em] leading-[1.02]">
              Your rights are <span className="text-[#39A8FF]">constitutional.</span>
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="font-inter text-white/70 text-base md:text-lg leading-relaxed">
              South African law places the burden on the State to justify an arrest. Where the police cannot, damages may be recoverable for the deprivation of liberty and its consequences.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
          {RIGHTS.map((r, i) => (
            <motion.article
              key={r.citation}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.6, delay: i * 0.07 }}
              data-testid={`right-card-${i}`}
              className="group relative overflow-hidden rounded-3xl border border-white/[0.06] bg-[#0d1826]/60 p-7 md:p-9 hover:border-[#0A84FF]/50 transition-colors"
            >
              <div className="flex items-start justify-between">
                <r.icon className="h-8 w-8 text-[#39A8FF]" strokeWidth={1.4} />
                <span className="font-poppins font-bold text-[#FFD400] text-sm tracking-wider">{r.citation}</span>
              </div>
              <h3 className="mt-8 font-poppins font-semibold text-white text-xl md:text-2xl leading-tight">{r.title}</h3>
              <p className="mt-3 font-inter text-white/65 text-[15px] leading-relaxed">{r.body}</p>
            </motion.article>
          ))}
        </div>

        <div className="mt-14 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 rounded-3xl border border-white/10 bg-[#0d1826]/70 p-6 md:p-8">
          <div className="flex items-start gap-4">
            <Landmark className="h-6 w-6 text-[#39A8FF] mt-0.5" strokeWidth={1.6} />
            <p className="font-inter text-white/80 max-w-2xl text-sm md:text-base">
              An arrest is not automatically unlawful. Whether a claim exists depends on the statutory grounds, the facts, and the applicable law. Our assessment tells you whether it is worth investigating.
            </p>
          </div>
          <Link
            to="/calculator"
            data-testid="rights-inline-cta"
            className="flex-shrink-0 inline-flex items-center gap-2 rounded-full bg-[#FFD400] px-6 py-3.5 text-xs font-poppins font-bold uppercase tracking-wider text-[#07111F]"
          >
            Check My Claim <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}
